import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, ChartColumn, ClipboardList, ClockAlert, Timer } from 'lucide-react';
import { APP_PATHS, appPath } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { EMPTY_REVIEW_FILTERS } from '@/modules/performance/hr-review-core/components/review-filters';
import { useGetSubmissions } from '@/modules/performance/hr-my-submissions/hooks/use-submissions';
import { useGetMySurveySubmissions } from '@/modules/surveys/my-responses/hooks/use-submissions';
import { SurveySubmissionsPagingResult } from '@/modules/surveys/my-responses/types/submission.types';
import {
  useGetAttendanceDays,
  useGetCorrectionStatusByDay,
} from '@/modules/time-attendance/attendance/hooks/use-attendance';
import { isCorrectableDay } from '@/modules/time-attendance/attendance/services/attendance-days';
import { DEFAULT_MY_TIMESHEETS_ORDER } from '@/modules/time-attendance/time-tracker/graphql/queries';
import { useGetMyTimeSheets } from '@/modules/time-attendance/time-tracker/hooks/use-my-timesheet';
import { useDayTarget } from '@/modules/time-attendance/time-tracker/hooks/use-under-time';
import { TimeSheetsPagingResult } from '@/modules/time-attendance/time-tracker/types/timesheet.types';
import {
  addDaysToDay,
  orgToday,
  startOfMonthDay,
  startOfWeekDay,
  taskDateRangeClause,
} from '@/modules/time-attendance/utils/task-date';
import { HomeCard, HomeList, HomeListRow } from '../home-card/home-card';

type Item = {
  key: string;
  icon: ReactNode;
  title: string;
  meta: string;
  /** Overdue or a missing day: a red icon square. */
  danger?: boolean;
  to: string;
  /** Soonest due first. */
  sortKey: string;
};

const PER_KIND = 4;

/**
 * Needs your attention (Component Map › Needs your attention): review forms and open surveys by due date,
 * this month's days that can still be corrected, and this week's Time Tracker days under the employee's
 * working hours. Each source shows only for those who may open its page.
 */
export const NeedsAttentionCard = ({ userId }: { userId: string }) => {
  const { t } = useTranslation();
  const today = orgToday();
  const canReview = useHasPermission(HRM_PERMISSIONS.PERFORMANCE.SUBMISSIONS_VIEW_OWN);
  const canSurvey = useHasPermission(HRM_PERMISSIONS.SURVEYS.RESPONSES_VIEW_OWN);
  const canAttendance = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_ATTENDANCE);
  const canTimesheet = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_TIME_TRACKER);

  // A query the user may not run would fail with an error toast: each source runs only with its
  // permission (an empty user id keeps the userId-keyed hooks idle).
  const reviews = useGetSubmissions(canReview ? userId : '', EMPTY_REVIEW_FILTERS, {
    pageNo: 1,
    pageSize: PER_KIND,
    columnWhere: { Status: 'Pending' },
  });
  const surveys = useGetMySurveySubmissions(
    { pageNo: 1, pageSize: PER_KIND, filter: { Status: 'Pending' } },
    canSurvey
  );

  // This month up to yesterday: the days that can still be corrected and have no correction yet.
  const monthStart = startOfMonthDay(today);
  const yesterday = addDaysToDay(today, -1);
  const attendanceUser = canAttendance && monthStart <= yesterday ? userId : '';
  const { data: monthDays } = useGetAttendanceDays(attendanceUser, monthStart, yesterday);
  const { data: correctionByDay } = useGetCorrectionStatusByDay(
    attendanceUser,
    monthStart,
    yesterday,
    Boolean(attendanceUser)
  );

  // This week's logged days under their working hours (the This week chart's own query).
  const weekStart = startOfWeekDay(today);
  const dayTarget = useDayTarget();
  const { data: weekData } = useGetMyTimeSheets(
    {
      pageNo: 1,
      pageSize: 50,
      filter: {
        UserId: userId,
        TaskDate: taskDateRangeClause(weekStart, addDaysToDay(weekStart, 6)),
      },
      order: DEFAULT_MY_TIMESHEETS_ORDER,
    },
    canTimesheet
  );

  const items: Item[] = [
    ...(canReview
      ? (reviews.data?.items ?? []).map(({ detail }) => ({
          key: `review-${detail.ItemId}`,
          icon: <ClipboardList />,
          title: detail.Name,
          meta: detail.PeersDeadLine
            ? t('HOME_DUE', { date: formatDateOnly(detail.PeersDeadLine) })
            : t('HOME_REVIEW_FORM'),
          danger: Boolean(detail.PeersDeadLine && detail.PeersDeadLine.slice(0, 10) < today),
          to: appPath.reviewSubmission(detail.ItemId),
          sortKey: detail.PeersDeadLine ?? '9999',
        }))
      : []),
    ...(canSurvey
      ? (
          (surveys.data as { SurveySubmissions?: SurveySubmissionsPagingResult })?.SurveySubmissions
            ?.items ?? []
        ).map((survey) => ({
          key: `survey-${survey.ItemId}`,
          icon: <ChartColumn />,
          title: survey.SurveyTitle ?? t('HOME_SURVEY'),
          meta: survey.Deadline
            ? t('HOME_DUE', { date: formatDateOnly(survey.Deadline) })
            : t('HOME_SURVEY'),
          danger: Boolean(survey.Deadline && survey.Deadline.slice(0, 10) < today),
          to: appPath.mySurveyResponse(survey.ItemId),
          sortKey: survey.Deadline ?? '9999',
        }))
      : []),
    ...(canAttendance
      ? (monthDays ?? [])
          .filter(
            (row) =>
              isCorrectableDay(row) &&
              correctionByDay?.[row.day] !== 'Pending' &&
              correctionByDay?.[row.day] !== 'Approved'
          )
          .map((row) => ({
            key: `correct-${row.day}`,
            icon: <ClockAlert />,
            title: t('HOME_DAY_TO_CORRECT'),
            meta: formatDateOnly(row.day),
            danger: true,
            to: APP_PATHS.ATTENDANCE,
            sortKey: row.day,
          }))
      : []),
    ...(canTimesheet
      ? ((weekData as { TimeSheets?: TimeSheetsPagingResult })?.TimeSheets?.items ?? [])
          .filter((record) => dayTarget(record.TaskDate.slice(0, 10), record.Tasks) === 'under')
          .map((record) => ({
            key: `under-${record.ItemId}`,
            icon: <Timer />,
            title: t('HOME_UNDER_TIME_DAY'),
            meta: formatDateOnly(record.TaskDate),
            to: APP_PATHS.TIME_TRACKER,
            sortKey: record.TaskDate.slice(0, 10),
          }))
      : []),
  ].sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  const isLoading = (canReview && reviews.isLoading) || (canSurvey && surveys.isLoading);

  return (
    // Two of the four columns on a wide screen.
    <HomeCard
      title={t('HOME_NEEDS_ATTENTION')}
      icon={<Bell />}
      count={items.length}
      className="sm:col-span-2 min-[1250px]:col-span-2"
    >
      <HomeList isLoading={isLoading} emptyText={t('HOME_ALL_CAUGHT_UP')}>
        {items.map((item) => (
          <HomeListRow
            key={item.key}
            icon={item.icon}
            tone={item.danger ? 'danger' : 'default'}
            title={item.title}
            meta={item.meta}
            to={item.to}
          />
        ))}
      </HomeList>
    </HomeCard>
  );
};
